import PeekEventBodyRow from './PeekEventBodyRow';
import PeekEventCommandBar from './PeekEventCommandBar';
import PeekEventLocationRow from './PeekEventLocationRow';
import PeekEventCollabSpaceRow from './PeekEventCollabSpaceRow';
import PeekEventOrganizerRow from './PeekEventOrganizerRow';
import PeekEventTimeRow from './PeekEventTimeRow';
import ShowMoreButton from './ShowMoreButton';
import TitleAndCharm from './TitleAndCharm';
import { observer } from 'mobx-react-lite';
import type { ViewType } from 'owa-calendar-actions';
import { getEffectiveCharm } from 'owa-calendar-charms-store';
import CalendarNameRow from 'owa-calendar-forms-common/lib/components/CalendarNameRow';
import { hasVisibleContentInBody } from 'owa-calendar-forms-utils/lib/utils/hasVisibleContentInBody';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { isFeatureEnabled } from 'owa-feature-flags';
import { JoinOnlineMeetingButton, isValidOnlineMeeting } from 'owa-online-meeting';
import type EventScope from 'owa-service/lib/contract/EventScope';
import type ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import * as React from 'react';
import {
    EmailInsightType,
    FileInsightType,
    InsightsDataFetchSource,
    isInsightsEnabledForEvent,
} from 'owa-meeting-insights-types-and-flights';
import {
    getAllInsightsEmails,
    getAllInsightsFiles,
    getFileAttachments,
    getEmailAttachments,
    getRelatedEmails,
    getRelatedFiles,
    InsightDocList,
    InsightDocSummary,
    hasInsightsToShowInTimePanelPeek,
    hasInsightsToShowInCalendarPeek,
    InsightsRenderLoggingHelper,
} from 'owa-meeting-insights';
import { getCalendarEventItemLocationText } from 'owa-calendar-event-attributes';
import { logUsage } from 'owa-analytics';
import { MobilityPeekRow, MobilityPeekTripOriginRow } from 'owa-mobility-read-view';
import { InfoBar } from 'owa-info-bar-view';
import getInfoBarMessageViewStates from '../utils/getInfoBarMessageViewStates';
import { isBrowserSafari } from 'owa-user-agent/lib/userAgent';
import type { ReadingPaneSection } from 'owa-calendar-helpers-types/lib/ReadingPaneSection';

import styles from './CalendarItemPeek.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface PeekEventContentProps {
    item: CalendarEvent;
    colorScheme: ColorScheme;
    actionSource: ViewType;
    onEditClicked: (eventScope: EventScope) => void;
    onDeleteClicked: (eventScope: EventScope) => void;
    onRespondClicked: (
        newResponseType: ResponseTypeType,
        eventScope: EventScope,
        sendMessage: boolean,
        notes: string
    ) => void;
    onDismiss?: () => void;
    onAttendeeTrackingClicked?: () => void;
    onReviewProposedNewTimeClicked?: (event?: Partial<CalendarEvent>) => void;
    allowFullBody: boolean;
    showInlineCommandBar: boolean;
    onOpenEventClicked: (
        ev?: React.MouseEvent<HTMLElement>,
        scrollToSection?: ReadingPaneSection
    ) => void;
}

export default observer(function PeekEventContent(props: PeekEventContentProps) {
    const {
        item,
        actionSource,
        colorScheme,
        onAttendeeTrackingClicked,
        onReviewProposedNewTimeClicked,
        allowFullBody,
        showInlineCommandBar,
        onOpenEventClicked,
    } = props;
    const locationDisplay = getCalendarEventItemLocationText(item);
    const previewText = item.Preview;
    const separator = (
        <div
            className={classNames(styles.separator, {
                isTimePanel: actionSource === 'TimePanelPeek',
            })}
            role="presentation"
        />
    );

    const charm = getEffectiveCharm(item.ParentFolderId, item.CharmId);

    const hasLocationRow = locationDisplay.length > 0 || item.IsRoomRequested;
    const hasOnlineMeetingRow = isValidOnlineMeeting(item);
    const hasOrganizerRow = item.IsMeeting && item.Organizer;
    const hasBodyRow: boolean = allowFullBody
        ? hasVisibleContentInBody(item)
        : previewText && previewText.length > 0;
    const eventId = item.ItemId.Id;

    const isInsightsEnabled = isInsightsEnabledForEvent(eventId);

    const isTimePanelPeek = actionSource === 'TimePanelPeek';
    const insightsEntrySource = isTimePanelPeek
        ? InsightsDataFetchSource.TimePanelEventDetails
        : InsightsDataFetchSource.ItemPeek;

    const hasInsightsToShow =
        (actionSource === 'SurfacePeek' && hasInsightsToShowInCalendarPeek(eventId)) ||
        (actionSource === 'TimePanelPeek' && hasInsightsToShowInTimePanelPeek(eventId));

    const infoBarViewStates = getInfoBarMessageViewStates(item);

    React.useEffect(() => {
        return () => {
            prevPeekProps.current = null;
        };
    }, []);

    // Set initial values
    const prevPeekProps = React.useRef({
        hasOnlineMeetingRow: hasOnlineMeetingRow,
        locationDisplay: locationDisplay,
        responseType: item.ResponseType,
    });

    // Check if any peek props changed
    React.useEffect(() => {
        // Checking prevPeekProps is null to make sure datapoint is only logged once
        if (!!prevPeekProps.current) {
            const hasOnlineMeetingRowChanged =
                prevPeekProps.current.hasOnlineMeetingRow != hasOnlineMeetingRow;
            const hasLocationsChanged = prevPeekProps.current.locationDisplay != locationDisplay;
            const hasResponseTypeChanged =
                !item.IsOrganizer && prevPeekProps.current.responseType != item.ResponseType; // only count responseType change for attendees because Yes/Maybe/No optinos don't show for organizer

            // Log PeekFlickered event if one of the properties changed since last render
            if (hasOnlineMeetingRowChanged || hasLocationsChanged || hasResponseTypeChanged) {
                logUsage('PeekFlickered', {
                    onlineMeeting: hasOnlineMeetingRowChanged,
                    location: hasLocationsChanged,
                    responseType: hasResponseTypeChanged,
                    actionSource: actionSource,
                });

                // Nullify prevPeekProps.current to ensure datapoint is logged only once in a component's life cycle
                prevPeekProps.current = null;
            }
        }
    }, [hasOnlineMeetingRow, locationDisplay, item.ResponseType]);

    return (
        <>
            {isInsightsEnabled && (
                <InsightsRenderLoggingHelper
                    eventId={eventId}
                    source={insightsEntrySource}
                    hasInsightsToShow={hasInsightsToShow}
                />
            )}
            <div
                className={
                    actionSource === 'TimePanelPeek'
                        ? classNames(styles.flexContainer, styles.peekScrollSection)
                        : null
                }>
                <TitleAndCharm
                    colorScheme={colorScheme}
                    event={item}
                    charmInfo={charm}
                    strikethrough={item.IsCancelled || item.ResponseType === 'Decline'}
                />
                {allowFullBody && infoBarViewStates?.length > 0 && (
                    <div className={styles.infoBarContainer}>
                        <InfoBar
                            className={styles.infoBar}
                            colorScheme={colorScheme}
                            textContainerClassName={styles.infobarText}
                            messages={infoBarViewStates}
                        />
                    </div>
                )}
                <PeekEventTimeRow
                    item={item}
                    onReviewProposedNewTimeClicked={onReviewProposedNewTimeClicked}
                    actionSource={actionSource}
                />
                {isFeatureEnabled('cal-mf-mobility') && (
                    <MobilityPeekTripOriginRow
                        calendarColorScheme={colorScheme}
                        actionSource={actionSource}
                    />
                )}
                {hasLocationRow && (
                    <PeekEventLocationRow
                        item={item}
                        colorScheme={colorScheme}
                        actionSource={actionSource}
                    />
                )}
                {!showInlineCommandBar && hasOnlineMeetingRow && (
                    <div className={styles.joinButtonContainer}>
                        <JoinOnlineMeetingButton
                            event={item}
                            colorScheme={colorScheme}
                            actionSource={actionSource}
                            className={styles.joinButton}
                            fullForm={'FormCompact'}
                        />
                    </div>
                )}
                {isFeatureEnabled('cal-mf-mobility') && <MobilityPeekRow />}
                <CalendarNameRow
                    event={item}
                    calendarNameRowStyle={classNames(
                        styles.calendarNameRow,
                        // need to cancel out top margin for visual alignment
                        showInlineCommandBar && styles.rowBeforeForInlineCommandBar
                    )}
                    calendarIconStyle={styles.calendarIcon}
                    calendarNameStyle={styles.calendarName}
                />
                {showInlineCommandBar && <PeekEventCommandBar {...props} />}
                {hasOrganizerRow && (
                    <>
                        {separator}
                        <PeekEventOrganizerRow
                            item={item}
                            onAttendeeTrackingClicked={onAttendeeTrackingClicked}
                        />
                    </>
                )}
                {(hasBodyRow || hasInsightsToShow) && (
                    <div
                        className={
                            actionSource === 'TimePanelPeek'
                                ? classNames(
                                      styles.peekScrollSection,
                                      styles.bodyAndInsightsSection,
                                      isBrowserSafari() && styles.peekScrollSectionSafari
                                  )
                                : null
                        }>
                        {renderBodyAndInsights(props, hasBodyRow, hasInsightsToShow, separator)}
                    </div>
                )}
                <PeekEventCollabSpaceRow
                    event={item}
                    colorScheme={colorScheme}
                    onOpenEventClicked={onOpenEventClicked}
                />
            </div>
        </>
    );
});

function renderBodyAndInsights(
    props: PeekEventContentProps,
    hasBodyRow: boolean,
    hasInsightsToShow: boolean,
    separator: JSX.Element
) {
    const { item, actionSource, colorScheme, allowFullBody, onOpenEventClicked } = props;

    const eventId = item.ItemId.Id;
    let bodyContent: JSX.Element = hasBodyRow && (
        <>
            {hasBodyRow && <>{separator}</>}
            <div className={styles.bodyContainer}>
                <PeekEventBodyRow
                    item={item}
                    colorScheme={colorScheme}
                    allowFullBody={allowFullBody}
                    actionSource={actionSource}
                />
            </div>
        </>
    );
    let content = bodyContent;

    if (hasInsightsToShow) {
        const isTimePanelPeek = actionSource === 'TimePanelPeek';
        const insightsEntrySource = isTimePanelPeek
            ? InsightsDataFetchSource.TimePanelEventDetails
            : InsightsDataFetchSource.ItemPeek;

        const allFiles = getAllInsightsFiles(eventId, insightsEntrySource);
        const fileAttachments = getFileAttachments(eventId, insightsEntrySource);
        const emailAttachments = getEmailAttachments(eventId, insightsEntrySource);
        const emailInsights = getAllInsightsEmails(eventId, insightsEntrySource);
        const relatedFiles = getRelatedFiles(eventId, insightsEntrySource);
        const relatedEmails = getRelatedEmails(eventId, insightsEntrySource);

        if (actionSource == 'SurfacePeek') {
            content = (
                <>
                    {bodyContent}
                    {allFiles.length > 0 && (
                        <>
                            {separator}
                            {renderCompactInsights(
                                eventId,
                                allFiles,
                                emailInsights,
                                actionSource,
                                colorScheme,
                                onOpenEventClicked
                            )}
                        </>
                    )}
                </>
            );
        } else if (actionSource == 'TimePanelPeek') {
            content = (
                <>
                    {fileAttachments.length + emailAttachments.length > 0 && (
                        <>
                            {separator}
                            {renderCompactInsights(
                                eventId,
                                fileAttachments,
                                emailAttachments,
                                actionSource,
                                colorScheme
                            )}
                        </>
                    )}
                    {bodyContent}
                    {relatedFiles.length + emailInsights.length > 0 && (
                        <>
                            {separator}
                            {renderCompactInsights(
                                eventId,
                                relatedFiles,
                                relatedEmails,
                                actionSource,
                                colorScheme
                            )}
                        </>
                    )}
                </>
            );
        }
    }

    return content;
}

function renderCompactInsights(
    meetingId: string,
    files: FileInsightType[],
    emails: EmailInsightType[],
    actionSource: ViewType,
    colorScheme: ColorScheme,
    onOpenEventClicked?: (
        ev: React.MouseEvent<HTMLElement>,
        scrollToSection?: ReadingPaneSection
    ) => void
): JSX.Element {
    const isTimePanelPeek = actionSource === 'TimePanelPeek';
    const insightsEntrySource = isTimePanelPeek
        ? InsightsDataFetchSource.TimePanelEventDetails
        : InsightsDataFetchSource.ItemPeek;
    const getShowMoreButton = () => {
        return ShowMoreButton;
    };
    const showInsightsInReadingPane = (ev: React.MouseEvent<HTMLElement>) => {
        if (onOpenEventClicked) {
            // scroll to insights only if meeting has no document attachment
            const shouldScrollToInsights =
                files.filter(file => file.isMeetingAttachment).length === 0;
            onOpenEventClicked(ev, shouldScrollToInsights ? 'MeetingInsights' : undefined);
        }
    };
    return isTimePanelPeek ? (
        <InsightDocList
            colorScheme={colorScheme}
            meetingId={meetingId}
            files={files}
            emails={emails}
            iconClass={styles.insightIconList}
            compactDocClass={classNames(styles.compactDoc, isTimePanelPeek && styles.timePanelPeek)}
            entrySource={insightsEntrySource}
            getShowMoreButton={getShowMoreButton}
        />
    ) : (
        <InsightDocSummary
            colorScheme={colorScheme}
            meetingId={meetingId}
            files={files}
            emails={emails}
            iconClass={styles.insightIcon}
            compactDocClass={classNames(styles.compactDoc, isTimePanelPeek && styles.timePanelPeek)}
            entrySource={insightsEntrySource}
            onSeeMoreCountClicked={showInsightsInReadingPane}
        />
    );
}
